import { Link } from 'react-router-dom';
import { fetchApiKeys, fetchCompany } from '../api/endpoints';
import { useApiData } from '../hooks/useApiData';

interface Step {
  key: string;
  label: string;
  hint: string;
  done: boolean;
  to: string;
  external?: boolean;
}

/**
 * "Get to live" checklist on the Dashboard for a Sandbox account (since 2026-10-23). Same steps,
 * wording and rules as the Razor partial Pages/Shared/_GoLiveChecklist.cshtml; keep the two the
 * same. Every tick comes from data the account already has: an API key, a verification, and the
 * live access request (Core ClientAccess). Shows nothing outside the Sandbox, or if either call
 * fails: it is a shortcut, not a page the dashboard depends on.
 */
export function GoLiveChecklist({ hasVerifications }: { hasVerifications: boolean }) {
  const company = useApiData(() => fetchCompany(), []);
  const keys = useApiData(fetchApiKeys, []);

  const access = company.data?.access;
  if (!access || access.stage !== 'Sandbox' || !keys.data) return null;

  const requested = access.kybStatus === 'Pending' || access.kybStatus === 'Approved';
  const steps: Step[] = [
    {
      key: 'key',
      label: 'Create a test API key',
      hint: 'Test keys are free and return masked data.',
      done: keys.data.length > 0,
      to: '/account/api-keys',
    },
    {
      key: 'verification',
      label: 'Run a test verification',
      hint: 'Follow the quickstart in the developer docs.',
      done: hasVerifications,
      to: '/docs',
      external: true,
    },
    {
      key: 'request',
      label: 'Request live access',
      hint:
        access.kybStatus === 'Pending'
          ? 'Under review. We reply within 24 hours.'
          : access.kybStatus === 'Rejected'
            ? 'Your last request was not approved. See Company for the reason.'
            : 'Tell us about your business. We reply within 24 hours.',
      done: requested,
      to: '/account/company',
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h6 className="mb-0">
          <i className="bi bi-rocket-takeoff me-2 text-primary" />
          Get to live
        </h6>
        <span className="badge badge-outline theme-secondary">
          {doneCount} of {steps.length} done
        </span>
      </div>
      <div className="list-group list-group-flush">
        {steps.map((s) => {
          const body = (
            <>
              <i
                className={`bi ${s.done ? 'bi-check-circle-fill text-success' : 'bi-circle text-body-secondary'} fs-5`}
              />
              <div className="flex-grow-1">
                <div className={`fw-medium ${s.done ? 'text-body-secondary text-decoration-line-through' : ''}`}>
                  {s.label}
                </div>
                <div className="small text-body-secondary">{s.hint}</div>
              </div>
              <i className="bi bi-chevron-right text-body-secondary small" />
            </>
          );
          const cls = 'list-group-item list-group-item-action d-flex align-items-center gap-3';
          return s.external ? (
            <a key={s.key} href={s.to} target="_blank" rel="noreferrer" className={cls}>
              {body}
            </a>
          ) : (
            <Link key={s.key} to={s.to} className={cls}>
              {body}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
